import React, { useState, useMemo, useCallback, useContext } from 'react';

import _ from 'lodash';

import { getMoment } from '@libs/NwMoment';

import { useEventsSearchContext } from '@contexts/EventsSearchContext';
import { ListContext } from '@contexts/ListContext';

import EventDrawer from '@components/EventDrawer/EventDrawer';
import GroupedBookingEvent from '@components/EventsBoxes/GroupedBookingEvent';
import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import { NwCard } from '@components/Gui/NwCard/NwCard';

import EventsSearchHeader from '@containers/Booking/SearchEvents/EventsSearchHeader';
import EventsSearchHeaderDrawer from '@containers/Booking/SearchEvents/EventsSearchHeaderDrawer';

const GroupedEventsSearchList = ({ events, loading, modelID, onFetchEvents }) => {
    const [selectedEvent, setSelectedEvent] = useState();

    const { sort, showTransactions, nameFilter, isDrawerMode } = useEventsSearchContext();
    const { cachedList } = useContext(ListContext);

    const projectsCustomFields = useMemo(() => {
        return cachedList.customFields.items['Project'];
    }, [cachedList.customFields]);

    const getProjectCategory = useCallback(
        (categoryID) => {
            const category = cachedList.projectCategories.items.find((category) => category.ID === categoryID);
            return category ? category.Name : '';
        },
        [cachedList.projectCategories]
    );

    // Ensure events has the correct grouped structure
    const eventsData = useMemo(() => {
        if (events && events.Events) {
            return events;
        }
        // If events is an array (not grouped yet), return empty structure
        return { Events: [], Projects: [] };
    }, [events]);

    const projects = useMemo(() => {
        const projectsList = [];

        // Use eventsData instead of events
        if (!eventsData.Events || eventsData.Events.length === 0) {
            return [];
        }

        const addEventToProject = (project, ev, notInResults) => {
            const getEventID = () => {
                if (ev.GroupGuid) {
                    return ev.GroupGuid;
                }
                if (ev.ID) {
                    return ev.ID;
                }
                if (ev.GroupedEventIDs && ev.GroupedEventIDs.length > 0) {
                    return ev.GroupedEventIDs[0];
                }
            };

            const getEventTitle = () => {
                if (ev.Title) {
                    return ev.Title;
                }
                if (ev.Name) {
                    return ev.Name;
                }
            };

            const getEventCancel = (eventtocheck) => {
                const myev = eventtocheck || ev;
                if (typeof myev.Cancel === 'boolean') {
                    return myev.Cancel;
                } else {
                    if (myev.Models) {
                        if (myev.Models.every((m) => m.Cancel)) {
                            return true;
                        }
                    }
                    return false;
                }
            };

            const updateProjectSortDate = (date) => {
                if (!project.SortDate || project.SortDate > date) {
                    project.SortDate = date;
                }
                if (!project.FirstDate) {
                    project.FirstDate = date;
                    project.LastDate = project.SearchEvents[0].SearchData.EndDate;
                }
            };

            const evtype = (ev.Type || ev.type).toLowerCase();
            const evsearchdata = {
                Type: evtype,
                InResults: notInResults ? false : true,
                ListKey: `${evtype}-${getEventID()}`,
                Title: getEventTitle(),
                Cancel: getEventCancel(),
            };

            const isEventPresent = project.SearchEvents.some((e) => e.SearchData.ListKey === evsearchdata.ListKey);

            if (!isEventPresent) {
                switch (evtype) {
                    case 'casting':
                    case 'job':
                    case 'ext':
                        project.SearchEvents.push({
                            ...ev,
                            Project: null,
                            SearchData: { ...evsearchdata, StartDate: ev.FirstDate, EndDate: ev.LastDate },
                        });
                        if (!project.NotInProject) {
                            project.Events = project.Events.filter((e) => e.ID !== ev.ID);
                        }
                        updateProjectSortDate(ev.FirstDate);
                        break;
                    case 'travel':
                        project.SearchEvents.push({
                            ...ev,
                            Project: null,
                            SearchData: { ...evsearchdata, StartDate: ev.StartDateTime, EndDate: ev.ArrivalDateTime },
                        });
                        if (!project.NotInProject) {
                            project.Travels = project.Travels.filter((e) => e.GroupGuid !== ev.GroupGuid);
                        }
                        updateProjectSortDate(ev.StartDateTime);
                        break;
                    case 'accommodation':
                        project.SearchEvents.push({
                            ...ev,
                            Project: null,
                            SearchData: { ...evsearchdata, StartDate: ev.FromDate, EndDate: ev.ToDate },
                        });
                        if (!project.NotInProject) {
                            project.Accommodations = project.Accommodations.filter((e) => e.GroupGuid !== ev.GroupGuid);
                        }
                        updateProjectSortDate(ev.FromDate);
                        break;
                    case 'followup':
                        project.SearchEvents.push({
                            ...ev,
                            Project: null,
                            SearchData: { ...evsearchdata, StartDate: ev.StartDate, EndDate: ev.EndDate },
                        });
                        if (!project.NotInProject) {
                            project.FollowUps = project.FollowUps.filter((e) => e.GroupGuid !== ev.GroupGuid);
                        }
                        updateProjectSortDate(ev.StartDate);
                        break;
                    case 'extjob':
                        project.SearchEvents.push({
                            ...ev,
                            Project: null,
                            SearchData: { ...evsearchdata, StartDate: ev.StartDate, EndDate: ev.EndDate },
                        });
                        updateProjectSortDate(ev.StartDate);
                        break;
                    case 'meeting':
                        project.SearchEvents.push({
                            ...ev,
                            Project: null,
                            SearchData: { ...evsearchdata, StartDate: ev.StartDate, EndDate: ev.EndDate },
                        });
                        if (!project.NotInProject) {
                            project.Meetings = project.Meetings.filter((e) => e.ID !== ev.ID);
                        }
                        updateProjectSortDate(ev.StartDate);
                        break;
                    default:
                        break;
                }
            } else {
                //event already present, update some values
                const eventpresent = project.SearchEvents.find((e) => e.SearchData.ListKey === evsearchdata.ListKey);
                if (eventpresent && !eventpresent.SearchData.InResults && notInResults) {
                    if (!eventpresent.Models) {
                        eventpresent.Models = [{ ModelID: eventpresent.ModelID, Cancel: eventpresent.Cancel }];
                        eventpresent.Cancel = null;
                        eventpresent.ModelID = null;
                    }
                    eventpresent.Models.push({ ModelID: ev.ModelID, Cancel: ev.Cancel });
                    eventpresent.SearchData.Cancel = getEventCancel(eventpresent);
                }
            }
        };

        if (eventsData.Events && eventsData.Events.length > 0) {
            let fakeeventcounter = 1;
            for (const ev of eventsData.Events) {
                if (ev.Project && ev.Project.ID) {
                    const project = projectsList.find((p) => p.ID === ev.Project.ID);
                    if (project) {
                        addEventToProject(project, ev);
                    } else {
                        //find project details in the Projects array
                        const projectDetails = eventsData.Projects.find((p) => p.ID === ev.Project.ID);
                        const newProject = { ...projectDetails, SearchEvents: [] };
                        addEventToProject(newProject, ev);
                        projectsList.push(newProject);
                    }
                } else {
                    const newFakeProject = { ID: `fake-${fakeeventcounter}`, NotInProject: true, SearchEvents: [] };
                    addEventToProject(newFakeProject, ev);
                    projectsList.push(newFakeProject);
                    fakeeventcounter++;
                }
            }
            for (const projectItem of projectsList) {
                //add all events to SearchEvents array if not already present
                if (projectItem.Events && projectItem.Events.length > 0) {
                    for (const ev of projectItem.Events) {
                        addEventToProject(projectItem, ev, true);
                    }
                }
                if (projectItem.Travels && projectItem.Travels.length > 0) {
                    for (const ev of projectItem.Travels) {
                        addEventToProject(projectItem, { ...ev, type: 'travel' }, true);
                    }
                }
                if (projectItem.Accommodations && projectItem.Accommodations.length > 0) {
                    for (const ev of projectItem.Accommodations) {
                        addEventToProject(projectItem, { ...ev, type: 'accommodation' }, true);
                    }
                }
                if (projectItem.Meetings && projectItem.Meetings.length > 0) {
                    for (const ev of projectItem.Meetings) {
                        addEventToProject(projectItem, { ...ev, type: 'meeting' }, true);
                    }
                }
                if (projectItem.FollowUps && projectItem.FollowUps.length > 0) {
                    for (const ev of projectItem.FollowUps) {
                        addEventToProject(projectItem, { ...ev, type: 'followup' }, true);
                    }
                }
                projectItem.SearchEvents = _.sortBy(projectItem.SearchEvents, [(ev) => ev.SearchData.StartDate]);
            }
            projectsList.sort((e1, e2) => {
                if (sort === 'time-up') {
                    if (!e1.SortDate) return -1;
                    if (!e2.SortDate) return 1;
                    return getMoment(e1.SortDate) > getMoment(e2.SortDate) ? 1 : -1;
                } else if (sort === 'time-down') {
                    if (!e1.SortDate) return 1;
                    if (!e2.SortDate) return -1;
                    return getMoment(e1.SortDate) < getMoment(e2.SortDate) ? 1 : -1;
                }
            });
            return projectsList;
        } else {
            return [];
        }
    }, [eventsData, sort, showTransactions]);

    // Populate ProjectCategoryName in projects
    const projectsWithCategoryNames = useMemo(() => {
        return projects.map((project) => {
            if (project.ProjectCategoryID) {
                return {
                    ...project,
                    ProjectCategoryName: getProjectCategory(project.ProjectCategoryID),
                };
            }
            return project;
        });
    }, [projects, getProjectCategory]);

    // Filter function for events - same logic as EventsSearchList
    const eventMatchesFilter = useCallback((event, filter, projectData = null) => {
        if (!filter) return true;

        const lowerFilter = filter.toLowerCase();

        // Check Title
        if (event.Title && event.Title.toLowerCase().includes(lowerFilter)) {
            return true;
        }

        // Check Name (used in grouped events)
        if (event.Name && event.Name.toLowerCase().includes(lowerFilter)) {
            return true;
        }

        // Check Description
        if (event.Description && event.Description.toLowerCase().includes(lowerFilter)) {
            return true;
        }

        // Check Project details (from event.Project if available)
        if (event.Project) {
            if (event.Project.Name && event.Project.Name.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.Project.Product && event.Project.Product.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (
                event.Project.ProjectCategoryName &&
                event.Project.ProjectCategoryName.toLowerCase().includes(lowerFilter)
            ) {
                return true;
            }
        }

        // Check Project details from parent project (for grouped events)
        if (projectData) {
            if (projectData.Name && projectData.Name.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (projectData.Product && projectData.Product.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (projectData.Description && projectData.Description.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (
                projectData.ProjectCategoryName &&
                projectData.ProjectCategoryName.toLowerCase().includes(lowerFilter)
            ) {
                return true;
            }
        }

        // Check type-specific fields
        const eventType = (event.type || event.Type || event.SearchData?.Type || '').toLowerCase();

        if (eventType === 'travel') {
            if (event.StartLocation && event.StartLocation.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.ArrivalLocation && event.ArrivalLocation.toLowerCase().includes(lowerFilter)) {
                return true;
            }
        }

        if (eventType === 'accommodation' || eventType === 'roomlodging') {
            if (event.Street && event.Street.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.City && event.City.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.Zip && event.Zip.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.CountryName && event.CountryName.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.State && event.State.toLowerCase().includes(lowerFilter)) {
                return true;
            }
        }

        if (eventType === 'extjob') {
            if (event.ClientName && event.ClientName.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.ProductName && event.ProductName.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.Location && event.Location.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.TimeSheet && event.TimeSheet.toLowerCase().includes(lowerFilter)) {
                return true;
            }
        }

        if (eventType === 'meeting') {
            if (event.MeetingContactName && event.MeetingContactName.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.Street && event.Street.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.City && event.City.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.Zip && event.Zip.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.CountryName && event.CountryName.toLowerCase().includes(lowerFilter)) {
                return true;
            }
            if (event.State && event.State.toLowerCase().includes(lowerFilter)) {
                return true;
            }
        }

        if (event.ServiceName && event.ServiceName.toLowerCase().includes(lowerFilter)) {
            return true;
        }

        return false;
    }, []);

    // Filter projects based on nameFilter
    const filteredProjects = useMemo(() => {
        if (!nameFilter || nameFilter.trim() === '') {
            return projectsWithCategoryNames;
        }

        return projectsWithCategoryNames
            .map((project) => {
                // Filter SearchEvents within each project
                const filteredEvents = project.SearchEvents.filter((event) =>
                    eventMatchesFilter(event, nameFilter, project)
                );

                if (filteredEvents.length === 0) {
                    return null;
                }

                return {
                    ...project,
                    SearchEvents: filteredEvents,
                };
            })
            .filter((project) => project !== null);
    }, [projectsWithCategoryNames, nameFilter, eventMatchesFilter]);

    const groupableEvents = ['followup', 'travel', 'accommodation'];

    const handleSelectEvent = (eventdata) => {
        let eventToSet = { ...eventdata };
        if (groupableEvents.includes(eventdata.SearchData.Type.toLowerCase())) {
            let idtoset = eventdata.GroupedEventIDs[0];
            if (modelID) {
                const selectedModel = eventdata.Models.find((model) => model.ModelID === modelID);
                if (selectedModel) {
                    switch (eventdata.SearchData.Type) {
                        case 'travel':
                            idtoset = selectedModel.TravelID;
                            break;
                        case 'accommodation':
                            idtoset = selectedModel.AccommodationID;
                            break;
                        case 'followup':
                            idtoset = selectedModel.FollowUpID;
                            break;
                        default:
                            break;
                    }
                }
            }
            eventToSet = { ...eventdata, ID: idtoset };
        }
        setSelectedEvent(eventToSet);
    };

    return (
        <>
            {isDrawerMode ? (
                <EventsSearchHeaderDrawer
                    events={eventsData.Events}
                    loading={loading}
                    eventsGrouped={true}
                    modelID={modelID}
                />
            ) : (
                <EventsSearchHeader
                    events={eventsData.Events}
                    loading={loading}
                    eventsGrouped={true}
                    modelID={modelID}
                />
            )}
            {loading ? (
                <Loading />
            ) : (
                <NwCard>
                    {filteredProjects.length > 0 ? (
                        filteredProjects.map((project) => (
                            <GroupedBookingEvent
                                key={project.ID}
                                placement="bottom"
                                loadEvents={onFetchEvents}
                                project={project}
                                expanded={true}
                                showTransactions={showTransactions}
                                onSelectEvent={handleSelectEvent}
                                page="event-search"
                                modelID={modelID}
                                projectsCustomFields={projectsCustomFields}
                            />
                        ))
                    ) : (
                        <EmptyState message="no events" />
                    )}
                </NwCard>
            )}
            {selectedEvent && (
                <EventDrawer
                    eventType={selectedEvent.SearchData.Type}
                    eventID={selectedEvent.ID}
                    eventObject={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                    afterUpdate={onFetchEvents}
                    afterRemove={onFetchEvents}
                />
            )}
        </>
    );
};

export default GroupedEventsSearchList;
